/**
 * Fehler mit der Meldung, die der Server geschickt hat. `field` bzw. `fields`
 * ordnen die Meldung einem Formularfeld zu.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly field: string | undefined;
  readonly fields: Record<string, string>;

  constructor(
    status: number,
    message: string,
    options: { field?: string; fields?: Record<string, string> } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.field = options.field;
    this.fields = options.fields ?? {};
  }
}

/**
 * Das CSRF-Token gehoert zur Session und wird bewusst nur im Speicher
 * gehalten -- nicht in localStorage, wo fremdes Skript es lesen koennte.
 */
let csrfToken = '';

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

const SAFE_METHODS = new Set(['GET', 'HEAD']);

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!SAFE_METHODS.has(method) && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    // Session-Cookie mitschicken -- gleiche Origin, daher 'same-origin'.
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; field?: string; fields?: Record<string, string> } })
      ?.error;
    throw new ApiError(response.status, error?.message ?? `Fehler ${response.status}.`, {
      ...(error?.field ? { field: error.field } : {}),
      ...(error?.fields ? { fields: error.fields } : {}),
    });
  }

  return payload as T;
}
